import { useEffect, useId } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, Heading3 } from 'lucide-react'
import { cn } from '@/lib/cn'
import { plainToRichHtml } from '@/lib/richText'

/**
 * Editor de texto rico (TipTap) restrito ao que o dompdf renderiza: negrito,
 * itálico, sublinhado, listas, subtítulo e link. Saída em HTML (getHTML()).
 * A sanitização definitiva é feita no backend (ProposalRichTextSanitizer).
 *
 * Quando `value` muda por fora (modal que reabre com outro registro), o
 * editor acompanha; enquanto a pessoa digita, quem manda é o editor.
 * Texto antigo, sem marcação, abre como parágrafos (uma linha, um parágrafo).
 */
export function RichTextEditor({ value, onChange, disabled, label, hint, required, compact, placeholder }: {
  value: string
  onChange: (html: string) => void
  disabled?: boolean
  label?: string
  hint?: string
  required?: boolean
  /** Altura menor, para descrições curtas. */
  compact?: boolean
  placeholder?: string
}) {
  const labelId = useId()
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({ link: false, underline: false, heading: { levels: [3, 4] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
    ],
    content: plainToRichHtml(value),
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? '' : editor.getHTML()),
    editorProps: {
      attributes: {
        ...(label ? { 'aria-labelledby': labelId } : {}),
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
        class: cn(
          compact ? 'min-h-[72px]' : 'min-h-[160px]',
          'px-3.5 py-2.5 outline-none text-sm text-gray-800 leading-relaxed',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_h3]:text-[15px] [&_h3]:font-semibold [&_h4]:font-semibold',
          '[&_a]:text-brand-600 [&_a]:underline',
        ),
      },
    },
  })

  useEffect(() => {
    if (!editor || editor.isFocused) return
    const next = plainToRichHtml(value)
    const atual = editor.isEmpty ? '' : editor.getHTML()
    if (next !== atual) editor.commands.setContent(next || '', { emitUpdate: false })
  }, [editor, value])

  if (!editor) return null

  const box = (
    <div className="rounded-md ring-1 ring-gray-200 bg-white overflow-hidden focus-within:ring-brand-300">
      {!disabled && (
        <div className="flex items-center gap-0.5 border-b border-gray-100 px-2 py-1.5">
          <Btn editor={editor} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} label="Negrito"><Bold size={14} /></Btn>
          <Btn editor={editor} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} label="Itálico"><Italic size={14} /></Btn>
          <Btn editor={editor} active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} label="Sublinhado"><UnderlineIcon size={14} /></Btn>
          <span className="w-px h-4 bg-gray-200 mx-1" />
          <Btn editor={editor} active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} label="Subtítulo"><Heading3 size={14} /></Btn>
          <Btn editor={editor} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Lista"><List size={14} /></Btn>
          <Btn editor={editor} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Lista numerada"><ListOrdered size={14} /></Btn>
          <span className="w-px h-4 bg-gray-200 mx-1" />
          <Btn editor={editor} active={editor.isActive('link')} onClick={() => setLink(editor)} label="Link"><Link2 size={14} /></Btn>
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  )

  if (!label && !hint) return box
  return (
    <div>
      {label && (
        <p id={labelId} className="mb-1.5 text-sm font-medium text-gray-700">
          {label}{required && <span className="text-rose-500"> *</span>}
        </p>
      )}
      {box}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  )
}

function setLink(editor: Editor) {
  const prev = editor.getAttributes('link').href as string | undefined
  const url = window.prompt('URL do link', prev ?? 'https://')
  if (url === null) return
  if (url === '') { editor.chain().focus().unsetLink().run(); return }
  editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

function Btn({ active, onClick, label, children }: {
  editor: Editor; active: boolean; onClick: () => void; label: string; children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        'cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded transition',
        active ? 'bg-brand-100 text-brand-700' : 'text-gray-500 hover:bg-gray-100',
      )}
    >
      {children}
    </button>
  )
}
